import { useState, useEffect } from 'react';
import { Music, Disc } from 'lucide-react';

const ListenBrainzFeed = ({ profile, allUsers }) => {
  const [nowPlaying, setNowPlaying] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    const fetchNowPlaying = async () => {
      try {
        const friendsEmails = (profile?.friends || []).map(e => e.toLowerCase());
        const usersToCheck = (allUsers || []).filter(u => 
          friendsEmails.includes(u.email?.toLowerCase()) || u.id === profile?.id
        );
        
        // Add auth user if not present (in case profile.id doesn't match or is missing)
        if (!usersToCheck.find(u => u.listenBrainzUsername === profile?.listenBrainzUsername) && profile?.listenBrainzUsername) {
          usersToCheck.push({ ...profile, name: 'You' });
        }

        const activeListeners = [];

        for (const u of usersToCheck) {
          if (!u.listenBrainzUsername) continue;
          try {
            const res = await fetch(`https://api.listenbrainz.org/1/user/${u.listenBrainzUsername}/playing-now`);
            if (!res.ok) continue;
            const data = await res.json();
            
            if (data?.payload?.listens && data.payload.listens.length > 0) {
              const listen = data.payload.listens[0];
              activeListeners.push({
                user: u,
                trackName: listen.track_metadata?.track_name || 'Unknown Track',
                artistName: listen.track_metadata?.artist_name || 'Unknown Artist',
                releaseName: listen.track_metadata?.release_name,
                playingNow: listen.playing_now
              });
            }
          } catch (e) {
            console.error(`Failed to fetch ListenBrainz for ${u.listenBrainzUsername}`, e);
          }
        }

        if (isMounted) {
          setNowPlaying(activeListeners);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching ListenBrainz feed:', err);
        if (isMounted) setLoading(false);
      }
    };

    fetchNowPlaying();
    const interval = setInterval(fetchNowPlaying, 60000); // Check every 1m
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [profile, allUsers]);

  if (loading && nowPlaying.length === 0) {
    return (
      <div className="flex flex-col gap-2 mb-4 animate-pulse">
        <h2 className="text-[10px] font-black uppercase text-zinc-400 tracking-[0.2em] px-1">Friend Activity</h2>
        <div className="h-12 bg-zinc-200 rounded-xl"></div>
      </div>
    );
  }

  if (nowPlaying.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 mb-4">
      <h2 className="text-[10px] font-black uppercase text-zinc-400 tracking-[0.2em] px-1 flex items-center gap-1">
        <Music size={10} className="text-pink-500" />
        Friend Activity
      </h2>
      <div className="flex flex-col gap-2">
        {nowPlaying.map((listen, idx) => (
          <div key={idx} className="bg-white border border-zinc-200 rounded-xl p-2.5 flex items-start gap-3 shadow-sm">
            <div className="w-8 h-8 rounded-full bg-pink-50 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Disc size={16} className="text-pink-500 animate-spin-slow" />
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-[10px] font-bold uppercase text-zinc-500 mb-0.5">
                {listen.user.name || listen.user.listenBrainzUsername} {listen.playingNow ? 'is listening to' : 'listened to'}
              </span>
              <span className="text-xs font-black text-zinc-900 truncate" title={listen.trackName}>
                {listen.trackName}
              </span>
              <span className="text-[11px] font-medium text-zinc-600 truncate" title={listen.artistName}>
                {listen.artistName}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ListenBrainzFeed;
